// =============================================================================
// GENERATED by scripts/build-shared.cjs from functions/src/shared/rentalLedgerMath.js
// DO NOT EDIT — edit the canonical file and re-run `node scripts/build-shared.cjs`.
// (TASK-10015: one rental-count math — the board, the pickup settle and the recount.)
//
// Side-effect module: attaches `window.RentalLedgerMath`. dispatch.js and
// roster.js read it at CALL time.
//
// The body below is the canonical file's PORTABLE CORE, copied byte for byte.
// =============================================================================
(function () {
  'use strict';

  // dayReturns / pendingPickups key for a move made by saveJob's create — no
  // single day of the range owns it. NOT '__job__': Firestore rejects any field
  // name that begins AND ends with "__", nested map keys included.
  const RLM_JOB_KEY = 'job';

  function rlmNum(v) {
    const x = Number(v);
    return Number.isFinite(x) && x > 0 ? x : 0;
  }

  function _rlmRecs(m, withTake) {
    const out = {};
    if (m && typeof m === 'object' && !Array.isArray(m)) {
      for (const k of Object.keys(m)) {
        const r = m[k];
        const qty = rlmNum(r && r.qty);
        if (qty <= 0) continue;
        if (!withTake) { out[k] = { qty }; continue; }
        const take = Math.min(rlmNum(r && r.take), qty);
        out[k] = { qty, take };
        // TASK-10006 S3 — the share of this pickup's spill (qty − take) that was
        // taken off its SOURCE jobs' `out` (fromJobs: { jobId: n }), capped.
        const fj = _rlmJobMap(r && r.fromJobs, qty - take);
        if (fj) out[k].fromJobs = fj;
      }
    }
    return out;
  }

  // { jobId: n } with positive numbers only, total capped at `cap`; null when empty.
  function _rlmJobMap(m, cap) {
    if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
    let left = cap == null ? Infinity : Math.max(0, Number(cap) || 0);
    const out = {};
    for (const k of Object.keys(m).sort()) {
      const n = Math.min(rlmNum(m[k]), left);
      if (n <= 0) continue;
      out[k] = n; left -= n;
    }
    return Object.keys(out).length ? out : null;
  }

  /** PURE. TASK-10006 S3 — how much of one pickup record was taken off its source jobs. */
  function rlmAttributed(rec) {
    const fj = rec && rec.fromJobs;
    if (!fj || typeof fj !== 'object') return 0;
    return Object.keys(fj).reduce((n, k) => n + rlmNum(fj[k]), 0);
  }

  /**
   * PURE. A ledger entry, normalised — garbage and negatives read as 0.
   *   out          units THIS job deployed and still holds
   *   parked       units given back when the job's last live day left (a restore re-deploys them)
   *   inspected    units handed to return inspections that are still open
   *   dayReturns   { [dayKey]: { qty, take } } immediate returns this job made
   *   heldReturns  same shape — immediate returns a CANCEL undid (restore re-applies)
   *   pendingPickups { [dayKey]: { qty } } — TASK-10015: pickups BOOKED for a day
   *                that has not come yet. No stock has moved for them.
   *   heldPickups  same shape — pending pickups a CANCEL set aside (restore puts them back)
   * pendingPickups / heldPickups appear only when non-empty, so every entry that
   * never had one reads exactly as it did before TASK-10015.
   */
  function rlmEntry(ledger, assetId) {
    const e = (ledger && typeof ledger === 'object' && ledger[assetId]) || {};
    const res = {
      out: rlmNum(e.out), parked: rlmNum(e.parked), inspected: rlmNum(e.inspected),
      dayReturns: _rlmRecs(e.dayReturns, true), heldReturns: _rlmRecs(e.heldReturns, true)
    };
    const pp = _rlmRecs(e.pendingPickups, false);
    const hp = _rlmRecs(e.heldPickups, false);
    if (Object.keys(pp).length) res.pendingPickups = pp;
    if (Object.keys(hp).length) res.heldPickups = hp;
    // TASK-10006 S3 — units of THIS job that a separate return job's real pickup
    // took off its `out` ({ returnJobId: n }). Settled history: the recount keeps it.
    const pb = _rlmJobMap(e.pickedUpBy, null);
    if (pb) res.pickedUpBy = pb;
    return res;
  }

  /** PURE. Drop empty pendingPickups / heldPickups maps from an entry. */
  function rlmTidy(entry) {
    const e = Object.assign({}, entry);
    if (!e.pendingPickups || !Object.keys(e.pendingPickups).length) delete e.pendingPickups;
    if (!e.heldPickups || !Object.keys(e.heldPickups).length) delete e.heldPickups;
    if (!e.pickedUpBy || !Object.keys(e.pickedUpBy).length) delete e.pickedUpBy;
    return e;
  }

  /** PURE. Does any entry of this ledger still hold a pickup waiting for its day? */
  function rlmLedgerHasPending(ledger) {
    if (!ledger || typeof ledger !== 'object' || Array.isArray(ledger)) return false;
    return Object.keys(ledger).some((k) => Object.keys(rlmEntry(ledger, k).pendingPickups || {}).length > 0);
  }

  /**
   * PURE. One deploy or return by THIS job, applied to its entry (TASK-9336).
   *   deploy            → out += q; a fresh (non-reversal) deploy clears `parked`.
   *   return            → the job hands over at most what it holds: `take`.
   *   immediate return  → also recorded under `dayKey` in dayReturns, so the day
   *   (dayKey given)      leaving later puts exactly those units back out.
   * recQty = what stock ACTUALLY moved (a floored decrement moves less than q).
   * An individually-tracked unit (not quantity mode) is one unit: out ≤ 1.
   */
  function rlmMovePlan(entry, direction, qty, isQty, isReversal, dayKey, recQty) {
    const q = isQty ? Math.max(0, Number(qty) || 0) : 1;
    const cap = (v) => (isQty ? v : Math.min(v, 1));
    const base = Object.assign({}, entry, {
      dayReturns: Object.assign({}, entry.dayReturns || {}),
      heldReturns: Object.assign({}, entry.heldReturns || {})
    });
    if (direction === 'deploy') {
      return { q, take: 0, entry: Object.assign({}, base, { out: cap(entry.out + q), parked: isReversal ? entry.parked : 0 }) };
    }
    const take = Math.min(entry.out, q);
    const next = Object.assign({}, base, { out: entry.out - take });
    if (dayKey && !isReversal && q > 0) {
      const prev = next.dayReturns[dayKey] || { qty: 0, take: 0 };
      const moved = Math.max(0, Math.min(q, recQty == null ? q : Number(recQty) || 0));
      if (moved > 0) next.dayReturns[dayKey] = Object.assign({}, prev, { qty: cap(prev.qty + moved), take: cap(prev.take + Math.min(take, moved)) });
    }
    return { q, take, entry: next };
  }

  /** 'YYYY-MM-DD' for an instant in an IANA zone (falls back to the runtime's local day). */
  function rlmDateKey(ms, tz) {
    const n = Number(ms);
    if (!Number.isFinite(n)) return '';
    const d = new Date(n);
    try {
      if (tz) return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    } catch (e) { /* bad zone string → local day */ }
    const p = (x) => (x < 10 ? '0' : '') + x;
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
  }

  /**
   * PURE. TASK-10006 D1 (Chris's reco): a booked pickup moves stock when it
   * HAPPENS, not when it is saved. A pickup dated today or earlier has happened;
   * one dated after today has not — it is held as "pickup planned" and moves on
   * its day (the hourly settle). No date known → due (the pre-TASK-10015
   * behaviour: move at save). Keys are tenant-local 'YYYY-MM-DD'.
   */
  function rlmPickupDue(pickupKey, todayKey) {
    const p = typeof pickupKey === 'string' ? pickupKey : '';
    const t = typeof todayKey === 'string' ? todayKey : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(p) || !/^\d{4}-\d{2}-\d{2}$/.test(t)) return true;
    return p <= t;
  }

  /**
   * PURE. TASK-10006 S3 (Chris 2026-10-22) — has the pickup on this day REALLY
   * happened? Stock moves on a real pickup, never on the date alone: the first
   * crew clock-in or a ticket on the return job's day, or the office's "Mark
   * picked up". The server settle stamps the day `rentalPickup: { dateKey, via,
   * at, by }` when it moves the stock; `pickedUp: true` is an in-memory
   * annotation (rlmAnnotatePickedUp). A day with neither has not happened.
   */
  function rlmDayPickedUp(d) {
    if (!d) return false;
    if (d.pickedUp === true) return true;
    const p = d.rentalPickup;
    return !!p && typeof p === 'object' && typeof p.dateKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.dateKey);
  }

  /**
   * PURE. TASK-10006 S3 — the job's days (with `dateKey`) for ONE rental, each
   * marked `pickedUp: true` when its pickup has happened:
   *   - the day carries the server's `rentalPickup` stamp (a real pickup), or
   *   - LEGACY: before S3 a pickup moved stock by its DATE, so a return that
   *     already moved (a dayReturns record under the day's id, or under the
   *     job-level key on a job none of whose days carries a stamp) and is dated
   *     today or earlier stays happened — the recount must not put back out what
   *     the old rule already brought home. A day still holding a planned pickup
   *     (pendingPickups under its id) never counts by the legacy rule, and a
   *     return dated AFTER today never does (a pickup taken early is undone —
   *     TASK-10015's D1 fix).
   * Returns a new array; a day is shallow-copied only when marked.
   */
  function rlmAnnotatePickedUp(days, entry, todayKey) {
    const list = Array.isArray(days) ? days : [];
    const e = entry || {};
    const dr = (e.dayReturns && typeof e.dayReturns === 'object') ? e.dayReturns : {};
    const pend = (e.pendingPickups && typeof e.pendingPickups === 'object') ? e.pendingPickups : {};
    const stamped = list.some((d) => rlmDayPickedUp(d));
    const has = (m, k) => Object.prototype.hasOwnProperty.call(m, k);
    const legacyJob = has(dr, RLM_JOB_KEY) && !stamped;
    const t = typeof todayKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(todayKey) ? todayKey : '';
    return list.map((d) => {
      if (!d) return d;
      if (rlmDayPickedUp(d)) return d.pickedUp === true ? d : Object.assign({}, d, { pickedUp: true });
      const legacy = (has(dr, d.id) || legacyJob) && !has(pend, d.id)
        && !!t && typeof d.dateKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.dateKey) && d.dateKey <= t;
      return legacy ? Object.assign({}, d, { pickedUp: true }) : d;
    });
  }

  /**
   * PURE. TASK-10006 S3 — one job's planned (not yet picked up) pickups, for the
   * board's "Pickup overdue" flag, the job's pickup panel and the project strip.
   *   ledger   job.rentalLedger
   *   days     the job's days, each with `dateKey`
   *   todayKey tenant-local today
   * A planned pickup is OVERDUE when its day is before today and no pickup has
   * happened — the units are still on site and the count says so.
   * Returns { items: [{ assetId, key, qty, dateKey, overdue }], overdue,
   *           overdueDayIds: { [dayId]: true }, earliestKey }.
   * Empty for a job with no planned pickup (every job without rentals).
   */
  function rlmJobPickupStatus(ledger, days, todayKey) {
    const res = { items: [], overdue: false, overdueDayIds: {}, earliestKey: '' };
    if (!ledger || typeof ledger !== 'object' || Array.isArray(ledger)) return res;
    const list = Array.isArray(days) ? days : [];
    const t = typeof todayKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(todayKey) ? todayKey : '';
    const late = (k) => !!t && /^\d{4}-\d{2}-\d{2}$/.test(k || '') && k < t;
    for (const aid of Object.keys(ledger)) {
      const pp = rlmEntry(ledger, aid).pendingPickups || {};
      for (const k of Object.keys(pp)) {
        let dateKey = '';
        let dueDays = [];
        if (k === RLM_JOB_KEY) {
          dueDays = list.filter((d) => rlmDayLive(d) && rlmDayCarries(d, aid) && rlmDayDir(d, aid) === 'return' && !rlmDayPickedUp(d))
            .sort((a, b) => String(a.dateKey || '').localeCompare(String(b.dateKey || '')));
          dateKey = dueDays.length ? String(dueDays[0].dateKey || '') : '';
        } else {
          const d = list.find((x) => x && x.id === k);
          if (d && rlmDayLive(d)) { dateKey = String(d.dateKey || ''); dueDays = [d]; }
        }
        const overdue = late(dateKey);
        if (overdue) {
          res.overdue = true;
          for (const d of dueDays) if (late(d.dateKey)) res.overdueDayIds[d.id] = true;
        }
        if (dateKey && (!res.earliestKey || dateKey < res.earliestKey)) res.earliestKey = dateKey;
        res.items.push({ assetId: aid, key: k, qty: rlmNum(pp[k].qty), dateKey, overdue });
      }
    }
    return res;
  }

  /** PURE. Book a pickup for a later day: no stock moves; the entry remembers it. */
  function rlmDeferPickupPlan(entry, dayKey, qty, isQty) {
    const q = isQty ? Math.max(0, Number(qty) || 0) : 1;
    const key = dayKey || RLM_JOB_KEY;
    const pend = Object.assign({}, entry.pendingPickups || {});
    const prev = pend[key] ? rlmNum(pend[key].qty) : 0;
    pend[key] = { qty: isQty ? prev + q : Math.min(prev + q, 1) };
    return { q, entry: Object.assign({}, entry, { pendingPickups: pend }) };
  }

  /**
   * PURE. The day of a planned pickup has come: take the pending record and
   * apply it as the immediate return it now is (rlmMovePlan with the same
   * dayKey, so that day leaving later undoes exactly what moved). recQty = what
   * the floored decrement can really move. null when nothing is pending there.
   */
  function rlmSettlePickupPlan(entry, dayKey, isQty, recQty) {
    const pend = Object.assign({}, entry.pendingPickups || {});
    const r = pend[dayKey];
    if (!r || rlmNum(r.qty) <= 0) return null;
    const qty = rlmNum(r.qty);
    delete pend[dayKey];
    const plan = rlmMovePlan(Object.assign({}, entry, { pendingPickups: pend }), 'return', qty, isQty, false, dayKey, recQty);
    return { qty, q: plan.q, take: plan.take, entry: rlmTidy(plan.entry) };
  }

  function rlmDayLive(d) {
    return !!d && String(d.status || '').toLowerCase() !== 'cancelled';
  }
  function rlmDayDir(d, assetId) {
    return (d && d.assetDirections && d.assetDirections[assetId]) === 'return' ? 'return' : 'deploy';
  }
  function rlmDayQty(d, assetId, isQty) {
    if (!isQty) return 1;
    return Math.max(1, Number(d && d.assetQtys && d.assetQtys[assetId]) || 1);
  }
  function rlmDayCarries(d, assetId) {
    return !!d && Array.isArray(d.assets) && d.assets.indexOf(assetId) >= 0;
  }

  /** PURE. What a job holds of `assetId` in one direction, read from its days:
   *  the largest quantity on any live day carrying it that way (days are copies,
   *  never summed). 'deploy' = units it has out; 'return' = units it picks up. */
  function rlmHolding(days, assetId, isQty, dir) {
    const want = dir === 'return' ? 'return' : 'deploy';
    let h = 0;
    for (const d of (Array.isArray(days) ? days : [])) {
      if (!rlmDayLive(d) || !rlmDayCarries(d, assetId) || rlmDayDir(d, assetId) !== want) continue;
      h = Math.max(h, rlmDayQty(d, assetId, isQty));
    }
    return h;
  }

  /** PURE. The deploy holding (rlmHolding 'deploy'). */
  function rlmDeployHolding(days, assetId, isQty) {
    return rlmHolding(days, assetId, isQty, 'deploy');
  }

  /**
   * PURE. How much one day's change moves the job's holding in direction `dir`
   * (default 'deploy'): H(days with `dayId` replaced by `nextDay`, or dropped
   * when nextDay is null) − H(days). Deploy: + = deploy more, − = give back.
   * Return: + = pick up more, − = put that many back out. Assign Crew and Edit
   * Job settle a quantity edit with this, and the recount reads the same
   * holding, so the two cannot disagree.
   */
  function rlmHoldingDelta(days, dayId, assetId, isQty, nextDay, dir) {
    const list = Array.isArray(days) ? days : [];
    const before = rlmHolding(list, assetId, isQty, dir);
    const after = list.filter((d) => d && d.id !== dayId);
    if (nextDay) after.push(Object.assign({}, nextDay, { id: dayId }));
    return rlmHolding(after, assetId, isQty, dir) - before;
  }

  /**
   * PURE. A pickup made SMALLER: take `r` units off this job's pickup records,
   * planned ones first (no stock had moved for them), then the immediate ones
   * (those units go back out: `redeploy`; the share that came out of the job's
   * own units goes back into `out`). `keys` = the order to draw from (the edited
   * day, then the job-level key, then the rest).
   */
  function rlmReducePickupPlan(entry, keys, r, isQty) {
    let left = Math.max(0, Number(r) || 0);
    const pend = Object.assign({}, entry.pendingPickups || {});
    const dr = Object.assign({}, entry.dayReturns || {});
    const order = [];
    for (const k of (Array.isArray(keys) ? keys : [])) if (k && order.indexOf(k) < 0) order.push(k);
    for (const k of Object.keys(pend).concat(Object.keys(dr))) if (order.indexOf(k) < 0) order.push(k);
    let unplanned = 0;
    for (const k of order) {
      if (left <= 0) break;
      if (!pend[k]) continue;
      const q = rlmNum(pend[k].qty);
      const cut = Math.min(q, left);
      left -= cut; unplanned += cut;
      if (q - cut > 0) pend[k] = { qty: q - cut }; else delete pend[k];
    }
    let redeploy = 0;
    let outBack = 0;
    for (const k of order) {
      if (left <= 0) break;
      if (!dr[k]) continue;
      const q = rlmNum(dr[k].qty);
      const t = Math.min(rlmNum(dr[k].take), q);
      // TASK-10006 S3 — the share taken off source jobs stays settled on them.
      const attr = Math.min(rlmAttributed(dr[k]), q - t);
      const cut = Math.min(q - attr, left);
      if (cut <= 0) continue;
      const tCut = Math.min(t, cut);
      left -= cut; redeploy += cut; outBack += tCut;
      if (q - cut > 0) dr[k] = Object.assign({}, dr[k], { qty: q - cut, take: t - tCut }); else delete dr[k];
    }
    const out = isQty ? rlmNum(entry.out) + outBack : Math.min(rlmNum(entry.out) + outBack, 1);
    return { unplanned, redeploy, outBack, left, entry: rlmTidy(Object.assign({}, entry, { out, dayReturns: dr, pendingPickups: pend })) };
  }

  /**
   * PURE. The PLANNED (not yet due) pickups a job's live days imply, per later
   * day, by the INCREASE each day makes to the pickup holding — chronologically
   * from R, the largest pickup that has already happened: 11/04 100 + 11/10 200
   * → { 11/04: 100, 11/10: 100 }, so each part moves on its own day (COD r6 🔴).
   * Keyed to the day, never job-level (the settle dates a job-level record by
   * the earliest return day, which may be past). `days` carry `dateKey`.
   * The recount and every pickup quantity edit use this, so both agree (COD r7).
   * Returns { pending: { [dayId]: { qty } }, R, P }.
   */
  function rlmPlannedPickups(days, assetId, isQty, todayKey) {
    const rets = (Array.isArray(days) ? days : [])
      .filter((d) => rlmDayLive(d) && rlmDayCarries(d, assetId) && rlmDayDir(d, assetId) === 'return')
      .sort((a, b) => String(a.dateKey || '').localeCompare(String(b.dateKey || '')) || String(a.id).localeCompare(String(b.id)));
    // TASK-10006 S3 — "happened" = the day's pickup really took place
    // (rlmDayPickedUp; callers annotate with rlmAnnotatePickedUp), never its date.
    const R = rets.filter((d) => rlmDayPickedUp(d)).reduce((m, d) => Math.max(m, rlmDayQty(d, assetId, isQty)), 0);
    const pending = {};
    let run = R;
    for (const d of rets) {
      if (rlmDayPickedUp(d)) continue;
      const q = rlmDayQty(d, assetId, isQty);
      if (q > run) { pending[d.id] = { qty: (pending[d.id] ? pending[d.id].qty : 0) + (q - run) }; run = q; }
    }
    return { pending, R, P: run };
  }

  /** PURE. Return inspections for one job + asset: units that came back through
   *  a CLOSED inspection (completed → returnedQty, dismissed → expectedQty — the
   *  amounts pretrip / maintenance take off deployedQty) and the ledger share
   *  still held by PENDING ones. */
  function rlmInspectionTotals(ris, isQty) {
    let closed = 0;
    let pendingShare = 0;
    for (const ri of (Array.isArray(ris) ? ris : [])) {
      const st = String((ri && ri.status) || '');
      if (st === 'completed') closed += isQty ? rlmNum(ri.returnedQty) : 1;
      else if (st === 'dismissed') closed += isQty ? rlmNum(ri.expectedQty) : 1;
      else if (st === 'pending') pendingShare += rlmNum(ri.ledgerQty);
    }
    return { closed, pendingShare };
  }

  /**
   * PURE. TASK-10015 — rebuild ONE job's ledger entry for ONE asset from what
   * its live days say, and the job's contribution to the asset's out-count.
   *   days       the job's days that carry the asset, each with `dateKey`
   *              ('YYYY-MM-DD', tenant-local) and `status`; [] for a job that is
   *              not live (deleted / cancelled)
   *   ris        this job's return inspections for the asset (any status)
   *   todayKey   tenant-local today
   *   needsInspection  asset.requireReturnInspection
   *   prev       the current entry (rlmEntry) — held* are kept (they describe
   *              CANCELLED days, which a restore puts back); parked only while
   *              no live day carries the rental
   * Pickup records are keyed to the return day when the job has exactly ONE live
   * return day for the rental, else job-level (RLM_JOB_KEY) — copies of a pickup
   * day are not separate pickups, so cancelling one copy must not undo it.
   * Returns { entry, contribution, deployQty, pickupQty, pendingQty, inspected }.
   *   contribution = units this job adds to (or, for a removal job, takes off)
   *                  the asset's out-count; equals rlmLedgerContribution(entry).
   */
  function rlmRebuildEntry(o) {
    const isQty = o.isQty !== false;
    const prev = o.prev || rlmEntry(null, o.assetId);
    // TASK-10006 S3 — a pickup counts once it has HAPPENED (stamp, or the legacy
    // date rule for a pickup that already moved stock) — rlmAnnotatePickedUp.
    // Annotated over ALL the job's days first (a cancelled day's stamp still
    // means the job's pickups are on the real-pickup rule — review #8), then
    // narrowed to the live days carrying the rental.
    const days = rlmAnnotatePickedUp((Array.isArray(o.days) ? o.days : []).filter((d) => rlmDayCarries(d, o.assetId)), prev, o.todayKey)
      .filter((d) => rlmDayLive(d));
    const D = rlmDeployHolding(days, o.assetId, isQty);
    const rets = days.filter((d) => rlmDayDir(d, o.assetId) === 'return')
      .sort((a, b) => String(a.dateKey || '').localeCompare(String(b.dateKey || '')) || String(a.id).localeCompare(String(b.id)));
    const keep = {};
    // `parked` = units given back when the job's last live day left; a restore
    // re-deploys them. When live days carry the rental again the recount IS the
    // re-deploy, so parked clears (as a fresh deploy clears it) — keeping it
    // would let a later restore deploy the same units a second time.
    if (rlmNum(prev.parked) > 0 && D === 0) keep.parked = prev.parked;
    const held = {
      heldReturns: Object.assign({}, prev.heldReturns || {}),
      heldPickups: Object.assign({}, prev.heldPickups || {})
    };
    // TASK-10006 S3 — settled cross-job pickups survive a rebuild: units a
    // separate return job's real pickup took off THIS job (pickedUpBy), and the
    // share of THIS job's pickups that came off source jobs (fromJobs).
    if (prev.pickedUpBy) keep.pickedUpBy = Object.assign({}, prev.pickedUpBy);
    const byOthersAll = Object.keys(prev.pickedUpBy || {}).reduce((n, k) => n + rlmNum(prev.pickedUpBy[k]), 0);
    const prevFrom = {};
    for (const k of Object.keys(prev.dayReturns || {})) {
      const fj = (prev.dayReturns[k] || {}).fromJobs || {};
      for (const j of Object.keys(fj)) prevFrom[j] = (prevFrom[j] || 0) + rlmNum(fj[j]);
    }
    if (o.needsInspection) {
      // Stock moves only when an inspection closes. The ledger hands each
      // inspection its share at creation (out → inspected) and, until TASK-10006
      // S1, never pays `inspected` down on close — so for these items
      // out + inspected = what the job deployed, and its contribution is that
      // minus what closed inspections took off the counter (rlmLedgerContribution).
      const it = rlmInspectionTotals(o.ris, isQty);
      const ownTake = Math.min(D, it.closed);
      const inspected = ownTake + Math.min(it.pendingShare, D - ownTake);
      const entry = rlmTidy(Object.assign({ out: D - inspected, parked: 0, inspected, dayReturns: {} }, keep, held));
      return { entry, contribution: D - it.closed, deployQty: D, pickupQty: it.closed, pendingQty: 0, inspected };
    }
    const due = rets.filter((d) => rlmDayPickedUp(d));
    const later = rets.filter((d) => !rlmDayPickedUp(d));
    const R = due.reduce((m, d) => Math.max(m, rlmDayQty(d, o.assetId, isQty)), 0);
    const P = later.reduce((m, d) => Math.max(m, rlmDayQty(d, o.assetId, isQty)), 0);
    const pendingQty = Math.max(0, P - R);
    const take = Math.min(D, R);
    const oneDay = rets.length === 1;
    const byOthers = Math.min(byOthersAll, D - take);
    const entry = Object.assign({ out: D - take - byOthers, parked: 0, inspected: 0, dayReturns: {} }, keep, held);
    let attrOwn = 0;
    if (R > 0) {
      const rec = { qty: R, take };
      const fj = _rlmJobMap(prevFrom, R - take);
      if (fj) { rec.fromJobs = fj; attrOwn = rlmAttributed(rec); }
      entry.dayReturns[oneDay ? due[0].id : RLM_JOB_KEY] = rec;
    }
    if (pendingQty > 0) entry.pendingPickups = rlmPlannedPickups(days, o.assetId, isQty, o.todayKey).pending;
    return { entry: rlmTidy(entry), contribution: D - R - byOthers + attrOwn, deployQty: D, pickupQty: R, pendingQty, inspected: 0, byOthers, attrOwn };
  }

  /** PURE. What a ledger entry says this job contributes to the out-count.
   *  Immediate-return items: units it holds (out + inspected) minus the pickups
   *  it made beyond its own units (dayReturns qty − take).
   *  Inspection items (opts.needsInspection, opts.ris): out + inspected minus
   *  what its closed inspections took off the counter (see rlmRebuildEntry). */
  function rlmLedgerContribution(entry, opts) {
    const e = entry || {};
    if (opts && opts.needsInspection) {
      return rlmNum(e.out) + rlmNum(e.inspected) - rlmInspectionTotals(opts.ris, opts.isQty !== false).closed;
    }
    let spill = 0;
    const dr = e.dayReturns || {};
    for (const k of Object.keys(dr)) spill += Math.max(0, rlmNum(dr[k].qty) - Math.min(rlmNum(dr[k].take), rlmNum(dr[k].qty)) - rlmAttributed(dr[k]));
    return rlmNum(e.out) + rlmNum(e.inspected) - spill;
  }

  function _rlmCanon(v) {
    if (Array.isArray(v)) return '[' + v.map(_rlmCanon).join(',') + ']';
    if (v && typeof v === 'object') {
      return '{' + Object.keys(v).sort().map((k) => JSON.stringify(k) + ':' + _rlmCanon(v[k])).join(',') + '}';
    }
    return JSON.stringify(v === undefined ? null : v);
  }
  function _rlmSameEntry(a, b) {
    return _rlmCanon(rlmTidy(a || {})) === _rlmCanon(rlmTidy(b || {}));
  }

  /**
   * PURE. TASK-10015 — recount ONE quantity-mode rental from live jobs.
   *   asset    { id, name, quantity, deployedQty, requireReturnInspection }
   *   jobs     [{ id, live, title, ledger (job.rentalLedger or null),
   *               projectId, returnForJobId, startKey (TASK-10006 S3 — links a
   *               return job's pickup to the jobs it came off, oldest first),
   *               days: [day docs carrying the asset, with dateKey],
   *               ris: [return inspections for this job + asset],
   *               txNet: Σ assetTransactions effect for this job + asset
   *                      (pre-ledger jobs: what they moved, for the explanation) }]
   *   todayKey tenant-local 'YYYY-MM-DD'
   * Returns { assetId, name, owned, current, recomputed, rawSum, changed,
   *           rows: [{ jobId, title, ledgerJob, before, after, outBefore, outAfter,
   *                    entryBefore, entryAfter, entryChanged, why }],
   *           older: [{ jobId, title, daysSay, rowsSay }],
   *           unexplained, overOwned, negative }
   *   Every live LEDGER job is rebuilt from its live days; the count moves by
   *   exactly Σ (after − before) over them: recomputed = current + that.
   *   `rows` lists only ledger jobs whose number or entry changes — "which jobs
   *   explain the difference". Jobs from before the ledger are NOT recounted
   *   (their labels are not proof stock moved) and are listed in `older`.
   *   `unexplained` = the part of today's count no live ledger job accounts for
   *   (older jobs, deleted jobs' history, earlier reconciles): current − Σ before.
   *   Idempotent: after an apply every ledger job's before == after.
   */
  function rlmRecountAsset(o) {
    const asset = o.asset || {};
    const isQty = asset.trackingMode == null || asset.trackingMode === 'quantity';
    const owned = Math.max(0, Number(asset.quantity) || 0);
    const current = Math.max(0, Number(asset.deployedQty) || 0);
    const rows = [];
    const older = [];
    let delta = 0;
    let sumBefore = 0;
    const built = [];
    for (const j of (Array.isArray(o.jobs) ? o.jobs : [])) {
      if (!j || !j.id) continue;
      const ledgerJob = !!j.ledger && typeof j.ledger === 'object' && !Array.isArray(j.ledger);
      const prev = ledgerJob ? rlmEntry(j.ledger, asset.id) : null;
      const rb = rlmRebuildEntry({
        assetId: asset.id, isQty, days: j.live === false ? [] : j.days, ris: j.ris,
        todayKey: o.todayKey, needsInspection: !!asset.requireReturnInspection, prev
      });
      if (!ledgerJob) {
        // A job from before the ledger (2026-09-23) is NOT recounted. Its day
        // labels are not proof stock moved (Edit Job flipped and re-quantified
        // them without moving stock — measured on a live tenant: removal jobs
        // labelled "deploy", installs edited 4,081 → 472), so a number read from
        // them would be a guess. Listed for the owner; its share of the count
        // stays as it is, and a yard count settles it.
        if (j.live !== false && rb.deployQty + rb.pickupQty + rb.pendingQty > 0) {
          older.push({ jobId: j.id, title: j.title || '', daysSay: rb.contribution, rowsSay: Number(j.txNet) || 0 });
        }
        continue;
      }
      built.push({ j, prev, rb, entry: rb.entry, after: rb.contribution,
        before: rlmLedgerContribution(prev, { needsInspection: !!asset.requireReturnInspection, ris: j.ris, isQty }) });
    }
    // TASK-10006 S3 — a pickup that happened on a separate RETURN job (same
    // project, or made from the job: returnForJobId, 5 levels) brought back
    // units its source jobs still show out. The live pickup step takes them off
    // the source jobs; a pickup recorded before that (or by the old date rule)
    // did not, so a later delete / cancel of the install job gave them back a
    // second time. The recount links them the same way, oldest source first.
    // The count itself does not move for this (the two records net out).
    const links = [];
    if (!asset.requireReturnInspection) {
      const byId = {};
      for (const x of built) byId[x.j.id] = x;
      // The sources' pickedUpBy is the durable record of a settled cross-job
      // pickup (a cancel of the return job's day drops ITS record — review #2):
      // re-derive the return job's fromJobs from it, capped at its spill.
      for (const x of built) {
        if (x.j.live === false) continue;
        const keys = Object.keys(x.entry.dayReturns || {});
        if (!keys.length) continue;
        const k = keys[0];
        const rec = x.entry.dayReturns[k];
        // Jobs the recount does not see (deleted) keep their recorded share
        // (independent review r2: dropping it made a later recount take the
        // still-out units of the job that remains).
        const fromSrc = Object.assign({}, rec.fromJobs || {});
        let seen = false;
        for (const y of built) {
          if (y === x) continue;
          const n = rlmNum((y.entry.pickedUpBy || {})[x.j.id]);
          if (n > 0) { fromSrc[y.j.id] = n; seen = true; } else if (fromSrc[y.j.id] != null) { delete fromSrc[y.j.id]; seen = true; }
        }
        if (!seen) continue;
        const fj = _rlmJobMap(fromSrc, Math.max(0, rlmNum(rec.qty) - Math.min(rlmNum(rec.take), rlmNum(rec.qty))));
        const next = Object.assign({}, rec);
        if (fj) next.fromJobs = fj; else delete next.fromJobs;
        x.entry = Object.assign({}, x.entry, { dayReturns: Object.assign({}, x.entry.dayReturns, { [k]: next }) });
      }
      for (const x of built) {
        if (x.j.live === false) continue;
        const keys = Object.keys(x.entry.dayReturns || {});
        if (!keys.length) continue;
        const k = keys[0];
        const rec = x.entry.dayReturns[k];
        let left = Math.max(0, rlmNum(rec.qty) - Math.min(rlmNum(rec.take), rlmNum(rec.qty)) - rlmAttributed(rec));
        if (left <= 0) continue;
        // The day the pickup happened (earliest happened return day): a source
        // that started AFTER it cannot be where these units came from (review #6).
        const pk = rlmAnnotatePickedUp((x.j.days || []).filter((d) => rlmDayCarries(d, asset.id)), x.prev, o.todayKey)
          .filter((d) => rlmDayLive(d) && rlmDayDir(d, asset.id) === 'return' && rlmDayPickedUp(d))
          .map((d) => String((d.rentalPickup && d.rentalPickup.dateKey) || d.dateKey || '')).filter(Boolean).sort()[0] || '';
        const anc = {};
        for (let a = x.j.returnForJobId, d = 0; a && d < 5 && !anc[a] && a !== x.j.id; d++) { anc[a] = true; a = (byId[a] && byId[a].j.returnForJobId) || null; }
        const srcs = built.filter((y) => y !== x && y.j.live !== false && rlmNum(y.entry.out) > 0
            && ((x.j.projectId && y.j.projectId === x.j.projectId) || anc[y.j.id])
            && (!pk || !y.j.startKey || String(y.j.startKey) <= pk))
          .sort((p, q) => String(p.j.startKey || '').localeCompare(String(q.j.startKey || '')) || String(p.j.id).localeCompare(String(q.j.id)));
        const al = rlmAllocatePickup(srcs.map((y) => ({ jobId: y.j.id, out: y.entry.out })), left);
        if (!al.parts.length) continue;
        const fj = Object.assign({}, rec.fromJobs || {});
        for (const part of al.parts) {
          const y = byId[part.jobId];
          const pb = Object.assign({}, y.entry.pickedUpBy || {});
          pb[x.j.id] = rlmNum(pb[x.j.id]) + part.qty;
          y.entry = Object.assign({}, y.entry, { out: rlmNum(y.entry.out) - part.qty, pickedUpBy: pb });
          y.after -= part.qty;
          fj[part.jobId] = rlmNum(fj[part.jobId]) + part.qty;
          x.after += part.qty;
          links.push({ returnJobId: x.j.id, sourceJobId: part.jobId, qty: part.qty, pickupDate: pk });
        }
        x.entry = Object.assign({}, x.entry, { dayReturns: Object.assign({}, x.entry.dayReturns, { [k]: Object.assign({}, rec, { fromJobs: fj }) }) });
      }
    }
    if (!asset.requireReturnInspection) {
      for (const x of built) x.after = x.j.live === false ? 0 : rlmLedgerContribution(x.entry);
    }
    for (const x of built) {
      const { j, prev, rb, before, after, entry } = x;
      delta += after - before;
      sumBefore += before;
      const entryChanged = !_rlmSameEntry(prev, entry);
      if (before === after && !entryChanged) continue;
      const why = [];
      if (j.live === false) why.push('job no longer live');
      if (rb.deployQty > 0 && rlmNum(prev.out) + rlmNum(prev.inspected) < rb.deployQty - rb.pickupQty && before < after) {
        why.push('its days carry ' + rb.deployQty + ' out but its record held ' + (rlmNum(prev.out) + rlmNum(prev.inspected)));
      }
      if (rb.pendingQty > 0) why.push('pickup of ' + rb.pendingQty + ' planned for a later day — not back yet');
      if (Object.keys(prev.pendingPickups || {}).length && !rb.pendingQty && rb.pickupQty) why.push('planned pickup has happened');
      if (!_rlmSameEntry({ pickedUpBy: prev.pickedUpBy }, { pickedUpBy: entry.pickedUpBy })) why.push('units brought back by a return job come off this job');
      if (!why.length) why.push(after > before ? 'holds more than its record' : after < before ? 'holds less than its record' : 'record tidied, count unchanged');
      rows.push({
        jobId: j.id, title: j.title || '', ledgerJob: true, before, after,
        outBefore: prev.out, outAfter: entry.out,
        entryBefore: prev, entryAfter: entry, entryChanged, why: why.join('; ')
      });
    }
    // The count moves by exactly what the live LEDGER jobs' records were off by.
    // Everything else in today's number (older jobs, deleted jobs' history,
    // earlier reconciles) stays as it is — `unexplained` says how much that is.
    const rawSum = current + delta;
    const recomputed = Math.max(0, rawSum);
    return {
      assetId: asset.id, name: asset.name || asset.partNumber || asset.id, owned, current,
      recomputed, rawSum, changed: recomputed !== current || rows.some((r) => r.entryChanged),
      rows, older, unexplained: current - sumBefore, overOwned: recomputed > owned, negative: rawSum < 0,
      links
    };
  }

  /**
   * PURE. TASK-10006 S3 — a real pickup on a separate return job brings back
   * units its SOURCE jobs still hold. Split `qty` over `sources` (already in
   * order: oldest first), each up to what it still has out.
   *   sources [{ jobId, out }]
   * Returns { parts: [{ jobId, qty }], left } (left = what no source holds).
   */
  function rlmAllocatePickup(sources, qty) {
    let left = Math.max(0, Number(qty) || 0);
    const parts = [];
    for (const s of (Array.isArray(sources) ? sources : [])) {
      if (left <= 0) break;
      if (!s || !s.jobId) continue;
      const n = Math.min(rlmNum(s.out), left);
      if (n <= 0) continue;
      parts.push({ jobId: s.jobId, qty: n });
      left -= n;
    }
    return { parts, left };
  }

  /** PURE. Signed effect of one assetTransactions row on the out-count:
   *  deploy +q, return −q, reversal ±delta, anything else 0 (pickup-planned
   *  moved nothing). The recount's explanation and the project count share it. */
  function rlmTxDelta(t) {
    const dir = String((t && (t.direction || t.type)) || '');
    const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
    if (dir === 'deploy') return Math.max(0, n(t.quantity));
    if (dir === 'return') return -Math.max(0, n(t.quantity));
    if (dir === 'reversal') return n(t.delta);
    return 0;
  }

  /** PURE. TASK-9263 — the Project Inventory tab's per-item quantities, from
   *  the project's assetTransactions rows. A deploy then a return of the same
   *  units is NOT twice the use: rentals count what is still out (rlmTxDelta:
   *  deploy +q, return −q, reversal ±delta; floored at 0), consumables count
   *  what a job used (`dispatch` / `supply_use` / `ticket`: signed qtyDelta
   *  netted like PpeReport's supply totals, legacy +quantity; floored at 0).
   *  Voided rows count nothing. `sent` / `returned` keep the round trip visible. */
  function rlmProjectQtyUsed(rows) {
    const n = (v) => Number(v) || 0;
    const map = new Map(); // free-text keys: never a plain {} (prototype keys)
    for (const t of (rows || [])) {
      const key = t && (t.assetId || t.assetName || t.partNumber);
      if (!key) continue;
      let m = map.get(key);
      if (!m) map.set(key, m = { name: t.assetName || t.partNumber || key, sent: 0, returned: 0, outNow: 0, consumed: 0, cost: 0, count: 0 });
      m.count++;
      if (t.voidedAt) continue;
      m.cost += Math.abs(n(t.totalCost));
      const dir = t.direction || t.type;
      // signed supply rows (qtyDelta: a use −q, an edit-down / reversal +q) net; legacy dispatch rows carry +quantity
      if (dir === 'dispatch' || dir === 'supply_use' || dir === 'ticket') m.consumed += t.qtyDelta != null ? -n(t.qtyDelta) : Math.abs(n(t.quantity));
      // same skip as rlmProjectRentalsOut: a scheduling row moved nothing
      else if (!/pending inspection/i.test(t.note || '')) {
        const d = rlmTxDelta(t);
        // a give-back reversal (−delta) un-sends; an undo-pickup reversal (+delta) un-returns
        if (dir === 'deploy' || (dir === 'reversal' && d < 0)) m.sent += d;
        if (dir === 'return' || (dir === 'reversal' && d > 0)) m.returned -= d;
        m.outNow += d;
      }
    }
    return Array.from(map.values(), (m) => (m.outNow = Math.max(0, m.outNow), m.consumed = Math.max(0, m.consumed), m.used = m.outNow + m.consumed, m));
  }

  /**
   * PURE. TASK-10006 — what a PROJECT still has out, per rental, for "Create
   * return job" and the project's "Out on this project" strip. A job with no
   * project passes just itself. Built ONLY from the numbers that already move
   * the count, so the strip, the prefill and the asset's out-count agree:
   *   - a LEDGER job counts rlmLedgerContribution (what it holds, minus the
   *     pickups it made beyond its own units — a removal job is negative), so
   *     installs + add-ons − pickups that have HAPPENED nets per project
   *     without writing anything onto the install jobs;
   *   - a job from before the ledger counts its own assetTransactions rows
   *     (rlmTxDelta; a "Return scheduled (pending inspection)" row moved
   *     nothing and is skipped), and is listed in `older` so the screen says so.
   * Not pickable again (subtracted from `pickable`, still `onSite`):
   *   planned    pickups booked for a later day (pendingPickups — D1: no stock
   *              has moved for them yet, but they are already on a return job)
   *   inspecting units on a PENDING return inspection (already picked up)
   * o = { jobs: [{ id, title, live, ledger, startKey }],
   *       assets: [roster asset docs], ris: [returnInspections of these jobs],
   *       txs: [assetTransactions rows of the jobs that have NO ledger],
   *       dayDates: { [jobDayId]: 'YYYY-MM-DD' } (to date a planned pickup) }
   * Returns { rows: [{ assetId, name, isQty, onSite, planned, plannedOn,
   *                    inspecting, pickable, sources: [{ jobId, title, qty }] }],
   *           older: [{ jobId, title }], pickableTotal }
   * rows = every rental the project has on site, planned or in inspection;
   * sources = the jobs behind onSite (+ holds, − pickups), oldest first.
   */
  function rlmProjectRentalsOut(o) {
    const jobs = (Array.isArray(o && o.jobs) ? o.jobs : []).filter((j) => j && j.id && j.live !== false)
      .slice().sort((a, b) => String(a.startKey || '').localeCompare(String(b.startKey || '')) || String(a.id).localeCompare(String(b.id)));
    const jobIds = {};
    for (const j of jobs) jobIds[j.id] = j;
    const isLedger = (j) => !!j.ledger && typeof j.ledger === 'object' && !Array.isArray(j.ledger);
    const dayDates = (o && o.dayDates) || {};
    const todayK = (o && typeof o.todayKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.todayKey)) ? o.todayKey : '';
    const ris = (Array.isArray(o && o.ris) ? o.ris : []).filter((r) => r && jobIds[r.jobId]);
    const txs = (Array.isArray(o && o.txs) ? o.txs : []).filter((t) => t && jobIds[t.jobId] && !isLedger(jobIds[t.jobId]));
    const rows = [];
    const olderIds = {};
    for (const a of (Array.isArray(o && o.assets) ? o.assets : [])) {
      if (!a || !a.id || !(a.quantityType === 'rental' || a.isRental)) continue;
      const isQty = a.trackingMode == null || a.trackingMode === 'quantity';
      const needsInspection = !!a.requireReturnInspection;
      let onSite = 0;
      let planned = 0;
      let overdue = 0;
      const plannedOn = {};
      const sources = [];
      for (const j of jobs) {
        let qty = 0;
        if (isLedger(j)) {
          const e = rlmEntry(j.ledger, a.id);
          const jr = ris.filter((r) => r.jobId === j.id && r.assetId === a.id);
          qty = isQty ? rlmLedgerContribution(e, { needsInspection, ris: jr, isQty }) : 0;
          for (const k of Object.keys(e.pendingPickups || {})) {
            const q = rlmNum(e.pendingPickups[k].qty);
            planned += q;
            const when = (k !== RLM_JOB_KEY && dayDates[k]) || j.startKey || '';
            plannedOn[when] = (plannedOn[when] || 0) + q;
            // TASK-10006 S3 — its day has passed and nobody picked it up.
            if (todayK && /^\d{4}-\d{2}-\d{2}$/.test(when) && when < todayK) overdue += q;
          }
        } else if (isQty) {
          for (const t of txs) {
            if (t.jobId !== j.id || t.assetId !== a.id) continue;
            if (/pending inspection/i.test(String(t.note || ''))) continue;
            qty += rlmTxDelta(t);
          }
          if (qty !== 0) olderIds[j.id] = j;
        }
        if (!isQty && a.deploymentStatus === 'deployed' && a.deployedToJobId === j.id) qty = 1;
        if (qty !== 0) { onSite += qty; sources.push({ jobId: j.id, title: j.title || '', qty }); }
      }
      let inspecting = 0;
      for (const r of ris) {
        if (r.assetId !== a.id || String(r.status || '') !== 'pending') continue;
        inspecting += isQty ? rlmNum(r.expectedQty) : 1;
      }
      onSite = Math.max(0, onSite);
      if (!isQty) { onSite = Math.min(onSite, 1); planned = Math.min(planned, 1); inspecting = Math.min(inspecting, 1); overdue = Math.min(overdue, 1); }
      if (onSite <= 0 && planned <= 0 && inspecting <= 0) continue;
      const pickable = Math.max(0, onSite - planned - inspecting);
      rows.push({
        assetId: a.id, name: a.name || a.partNumber || a.id, isQty, onSite, planned, overdue: Math.min(overdue, planned),
        plannedOn: Object.keys(plannedOn).sort().map((k) => ({ dateKey: k, qty: plannedOn[k] })),
        inspecting, pickable, sources
      });
    }
    rows.sort((x, y) => String(x.name).localeCompare(String(y.name)));
    return {
      rows,
      older: Object.keys(olderIds).map((id) => ({ jobId: id, title: olderIds[id].title || '' })),
      pickableTotal: rows.reduce((n, r) => n + r.pickable, 0)
    };
  }

  /**
   * PURE. A day — or, with selfId null + days [] + mode 'job-delete', a whole
   * job — leaving, for ONE rental. Moved here VERBATIM from dispatch.js
   * `_rentalLedgerLeavePlan` (integ/2113) so the browser and the server's
   * deleteJobLedgered (TASK-10010, rentals: 'server') run ONE copy; dispatch.js
   * keeps that name as a thin delegate.
   *   days       — the job's OTHER days as committed; the leaving day is excluded by selfId.
   *   pendingRis — the job's PENDING return inspections for this rental.
   *   mode       — 'cancel' keeps what it undoes in heldReturns / heldPickups for a restore;
   *                'job-delete' holds nothing.
   * Order: (1) undo the immediate returns this day made (and the job-level ones
   * when it is the last holder) — those units go back OUT, `redeploy`; (2) cancel
   * the inspections this day owns (+ orphans/job-level when last holder), their
   * ledgerQty back to `out`; (3) if no other live day carries the rental, give
   * back exactly `out`. Stock changes by redeploy − giveBack. cancelRis = ids.
   */
  function rlmLeavePlan({ assetId, selfId, days, entry, pendingRis, isQty, mode, stillHeld }) {
    const list = Array.isArray(days) ? days : [];
    const isLive = (d) => !!d && d.status !== 'cancelled';
    const liveIds = new Set(list.filter(d => isLive(d) && d.id !== selfId).map(d => d.id));
    // stillHeld: a return→deploy FLIP — this day keeps carrying the rental, so
    // nothing is given back and job-level records stay put (COD R2 🔴).
    const otherLive = !!stillHeld || list.some(d => isLive(d) && d.id !== selfId
      && Array.isArray(d.assets) && d.assets.includes(assetId));
    const cap = (v) => (isQty ? v : Math.min(v, 1));
    const dayReturns = { ...(entry.dayReturns || {}) };
    const heldReturns = { ...(entry.heldReturns || {}) };
    let out = entry.out;
    let redeploy = 0;
    let restored = 0;   // units put back in `out` from this day's own undone return / closed inspections
    const undoKeys = [];
    for (const k of Object.keys(dayReturns)) {
      const orphan = k === RLM_JOB_KEY || (k !== selfId && !liveIds.has(k));
      if (k === selfId || (orphan && !otherLive)) undoKeys.push(k);
    }
    for (const k of undoKeys) {
      const r = dayReturns[k];
      // TASK-10006 S3 — the share of a real pickup that was taken off a SOURCE
      // job's `out` stays settled there (that job's out already went down):
      // undoing this record puts back only the rest, and holds only the rest.
      const attr = Math.min(rlmAttributed(r), Math.max(0, r.qty - r.take));
      redeploy += r.qty - attr; out += r.take; restored += r.take;
      delete dayReturns[k];
      // Hold what a restore must re-apply. A job-level/orphan record was undone
      // only because the LAST holder left, so it is held in EVERY mode (a later
      // restore of any cancelled day re-applies it — review 🔴: dropped on
      // remove/unassign, it stranded the pickup's units as deployed); it is
      // re-keyed to the job key so any restored day picks it up. The day's own
      // record is held only by a cancel — a removed/unassigned day's return is gone.
      const holdKey = k === selfId ? (mode === 'cancel' ? k : null) : RLM_JOB_KEY;
      if (holdKey && mode !== 'job-delete') {
        const prev = heldReturns[holdKey] || { qty: 0, take: 0 };
        if (r.qty - attr > 0) heldReturns[holdKey] = { qty: cap(prev.qty + r.qty - attr), take: cap(prev.take + r.take) };
      }
    }
    const cancelRis = [];
    let inspectedLeft = Math.max(0, Number(entry.inspected) || 0);
    for (const ri of (Array.isArray(pendingRis) ? pendingRis : [])) {
      if (!ri || ri.assetId !== assetId) continue;
      const owner = ri.jobDayId || null;
      const mine = owner === selfId;
      const orphan = !owner || (!mine && !liveIds.has(owner));
      if (mine || (orphan && !otherLive)) {
        cancelRis.push(ri.id);
        const raw = Number(ri.ledgerQty);
        const lq = Math.min(Number.isFinite(raw) && raw > 0 ? raw : 0, inspectedLeft);
        inspectedLeft -= lq;
        out += lq;
        restored += lq;
      }
    }
    out = cap(out);
    redeploy = cap(redeploy);
    let parked = entry.parked;
    let giveBack = 0;
    if (!otherLive) { giveBack = out; parked = cap(parked + out); out = 0; }
    // TASK-10015 — pickups booked for a later day moved NO stock, so a day
    // leaving simply takes its own (and, as the last holder, the job-level /
    // orphaned ones) off the books. A cancel sets them aside for a restore.
    const pendingPickups = { ...(entry.pendingPickups || {}) };
    const heldPickups = { ...(entry.heldPickups || {}) };
    for (const k of Object.keys(pendingPickups)) {
      const orphan = k === RLM_JOB_KEY || (k !== selfId && !liveIds.has(k));
      if (!(k === selfId || (orphan && !otherLive))) continue;
      // Held exactly like dayReturns above: a job-level / orphaned plan is set
      // aside in every mode (a later restore of any cancelled day brings it
      // back — review: dropping it on remove lost the booked pickup), this
      // day's own plan only by a cancel; a whole-job delete holds nothing.
      const hk = k === selfId ? (mode === 'cancel' ? k : null) : RLM_JOB_KEY;
      if (hk && mode !== 'job-delete') {
        const prevQ = heldPickups[hk] ? Number(heldPickups[hk].qty) || 0 : 0;
        heldPickups[hk] = { qty: cap(prevQ + (Number(pendingPickups[k].qty) || 0)) };
      }
      delete pendingPickups[k];
    }
    const kept = entry.pickedUpBy ? { pickedUpBy: entry.pickedUpBy } : {};
    return { otherLive, cancelRis, redeploy, giveBack, restored,
      entry: rlmTidy(Object.assign({ out, parked, inspected: inspectedLeft, dayReturns, heldReturns, pendingPickups, heldPickups }, kept)) };
  }

  /**
   * PURE. One rental's stock change (integ/2113: the decision half of dispatch.js
   * `_txGiveBackRental`, shared with the server's job delete / restore).
   * delta > 0 = units go back OUT on jobId, < 0 = back to the yard.
   *   quantity mode → deployedQty floored at 0 (always written when delta ≠ 0);
   *   a unit        → out: refused when it is deployed on ANOTHER job;
   *                   home: released only when it is out on this job or on nothing.
   * Returns { write, applied, before, after }: write = the field update (null =
   * nothing to write), applied = what really moved (floored change, or ±1).
   */
  function rlmStockChange(aData, delta, jobId) {
    const a = aData || {};
    if (!delta) return { write: null, applied: 0 };
    if (a.trackingMode === 'quantity') {
      const cur = Number(a.deployedQty) || 0;
      const next = Math.max(0, cur + delta);
      return { write: { deployedQty: next }, applied: next - cur, before: { deployedQty: cur }, after: { deployedQty: next } };
    }
    const before = { deploymentStatus: a.deploymentStatus || null, deployedToJobId: a.deployedToJobId || null };
    if (delta > 0) {
      if (a.deploymentStatus === 'deployed' && a.deployedToJobId && a.deployedToJobId !== jobId) return { write: null, applied: 0, before, after: before };
      const after = { deploymentStatus: 'deployed', deployedToJobId: jobId };
      return { write: after, applied: 1, before, after };
    }
    if (!a.deployedToJobId || a.deployedToJobId === jobId) {
      const after = { deploymentStatus: 'available', deployedToJobId: null };
      return { write: after, applied: -1, before, after };
    }
    return { write: null, applied: 0, before, after: before };
  }

  window.RentalLedgerMath = Object.freeze({
    JOB_KEY: RLM_JOB_KEY,
    rlmNum: rlmNum,
    rlmEntry: rlmEntry,
    rlmTidy: rlmTidy,
    rlmLedgerHasPending: rlmLedgerHasPending,
    rlmMovePlan: rlmMovePlan,
    rlmDateKey: rlmDateKey,
    rlmPickupDue: rlmPickupDue,
    rlmDeferPickupPlan: rlmDeferPickupPlan,
    rlmSettlePickupPlan: rlmSettlePickupPlan,
    rlmDayLive: rlmDayLive,
    rlmDayDir: rlmDayDir,
    rlmDayQty: rlmDayQty,
    rlmDayCarries: rlmDayCarries,
    rlmHolding: rlmHolding,
    rlmDeployHolding: rlmDeployHolding,
    rlmHoldingDelta: rlmHoldingDelta,
    rlmReducePickupPlan: rlmReducePickupPlan,
    rlmPlannedPickups: rlmPlannedPickups,
    rlmInspectionTotals: rlmInspectionTotals,
    rlmRebuildEntry: rlmRebuildEntry,
    rlmLedgerContribution: rlmLedgerContribution,
    rlmRecountAsset: rlmRecountAsset,
    rlmTxDelta: rlmTxDelta,
    rlmProjectRentalsOut: rlmProjectRentalsOut,
    rlmProjectQtyUsed: rlmProjectQtyUsed,
    rlmLeavePlan: rlmLeavePlan,
    rlmStockChange: rlmStockChange,
    rlmDayPickedUp: rlmDayPickedUp,
    rlmAnnotatePickedUp: rlmAnnotatePickedUp,
    rlmJobPickupStatus: rlmJobPickupStatus,
    rlmAttributed: rlmAttributed,
    rlmAllocatePickup: rlmAllocatePickup
  });
})();
